import React, { useCallback, useEffect, useRef, useState } from "react";
import classnames from "classnames";
import { useSelector } from "react-redux";
import { Image } from "@fluentui/react";
import { Label, Loader } from "src/ui";
import { localize } from "src/l10n";
import { SpintrTypes } from "src/typings";
import { MarketplaceWidgetData } from "src/marketplace/components/MarketplaceWidgetData";
import { MarketplaceWidgetErrorBoundary } from "src/marketplace/components/MarketplaceWidgetErrorBoundary";
import { studioApi } from "src/studio/api/studioApi";
// Side-effect import: reuse MarketplaceWidget chrome so Studio widgets are visually identical.
import "src/marketplace/components/MarketplaceWidget/MarketplaceWidget.scss";

type Source =
    | { source: "published"; appId: number }
    | { source: "draft"; appId: number; versionId: number; widgetType: number; actionName: string };

type Props = Source & {
    appName?: string;
    autoWidth?: boolean;
    displayBorder?: boolean;
};

interface State {
    loading: boolean;
    error: string | null;
    displayType: number | null;
    data: unknown;
    config: unknown;
    name: string | null;
    iconUrl: string | null;
    variables: Record<string, string>;
}

const initialState: State = {
    loading: true,
    error: null,
    displayType: null,
    data: null,
    config: null,
    name: null,
    iconUrl: null,
    variables: {},
};

// Mirrors StudioAppsController.BuildWidgetDataProjection — widgets read every variable as a string.
function coerceVariables(raw: unknown): Record<string, string> {
    if (!raw || typeof raw !== "object") return {};
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
        if (typeof v === "string") out[k] = v;
        else if (typeof v === "number") out[k] = String(v);
        else if (v === true) out[k] = "1";
        else if (v === false) out[k] = "0";
    }
    return out;
}

export const StudioWidget: React.FC<Props> = (props) => {
    const [state, setState] = useState<State>(initialState);
    const fallbackInstanceIcon = useSelector<Spintr.AppState, string | undefined>(
        (s) => s.instance.get("faviconUrl") || s.instance.get("logoUrl") || undefined,
    );
    const draftIconUrl = useSelector<Spintr.AppState, string | null>(
        (s) => (s.studio?.session.iconUrl as string | undefined) ?? null,
    );
    const iconUrl = state.iconUrl ?? fallbackInstanceIcon;

    // Narrow to primitives so `load` isn't re-created on every parent render during streaming.
    const draftVersionId = props.source === "draft" ? props.versionId : null;
    const draftWidgetType = props.source === "draft" ? props.widgetType : null;
    const draftActionName = props.source === "draft" ? props.actionName : null;
    const draftAppName = props.source === "draft" ? props.appName ?? null : null;

    // Read the latest draftIconUrl at load time without depending on it — depending
    // would re-create `load` every time the icon URL changes during streaming.
    const draftIconUrlRef = useRef(draftIconUrl);
    useEffect(() => { draftIconUrlRef.current = draftIconUrl; }, [draftIconUrl]);

    const load = useCallback(async () => {
        setState({ ...initialState, loading: true });
        try {
            if (props.source === "published") {
                const result = await studioApi.getWidgetData(props.appId);
                setState({
                    loading: false,
                    error: null,
                    displayType: result.displayType,
                    data: result.data,
                    config: result.config,
                    name: result.name,
                    iconUrl: result.iconUrl ?? null,
                    variables: result.variables ?? {},
                });
                return;
            }

            // Draft preview hits executeAction directly so unpublished changes are visible.
            // Always the workbench surface (the published path returns above), so pin "staging" —
            // right after a publish the draft version IS the published one, and inferring from the
            // version would silently run this preview against live data.
            const raw = await studioApi.executeAction(draftVersionId!, draftActionName!, {}, "staging");
            const payload = raw.result as { name?: string; data?: unknown; config?: unknown; variables?: unknown } | undefined;
            if (!payload || typeof payload !== "object" || !("data" in payload)) {
                setState({
                    ...initialState,
                    loading: false,
                    error: "INVALID_SHAPE",
                });
                return;
            }
            setState({
                loading: false,
                error: null,
                displayType: draftWidgetType!,
                data: payload.data,
                config: payload.config ?? {},
                name: (typeof payload.name === "string" && payload.name.trim()) || draftAppName || null,
                iconUrl: draftIconUrlRef.current,
                variables: coerceVariables(payload.variables),
            });
        } catch {
            setState({
                ...initialState,
                loading: false,
                error: "LOAD_FAILED",
            });
        }
    }, [props.source, props.appId, draftVersionId, draftWidgetType, draftActionName, draftAppName]);

    useEffect(() => { load(); }, [load]);

    const isLeaderboard = state.displayType === SpintrTypes.WidgetType.Leaderboard;

    return (
        <div className={classnames("MarketplaceWidget", "StudioWidget", {
            autoWidth: props.autoWidth,
            displayBorder: !!props.displayBorder,
            isLeaderboard,
        })}>
            <MarketplaceWidgetErrorBoundary>
                <div className="information start-page-flexible-padding">
                    {iconUrl && (
                        <div className="logo">
                            <Image height={24} width={24} src={iconUrl} />
                        </div>
                    )}
                    <div className="name">
                        <Label as="div" size="body-2" weight="medium" color="visageGray2">
                            {state.name ?? ""}
                        </Label>
                    </div>
                </div>
                <div className="data start-page-flexible-padding">
                    {state.loading && <Loader />}
                    {!state.loading && state.error && (
                        <Label className="widget-error" color="mid-grey" size="body-2">
                            {localize("TeknisktFel")}
                        </Label>
                    )}
                    {!state.loading && !state.error && state.displayType !== null && (
                        <MarketplaceWidgetData
                            config={state.config}
                            data={state.data}
                            displayType={state.displayType as Spintr.WidgetType}
                            variables={state.variables}
                        />
                    )}
                </div>
            </MarketplaceWidgetErrorBoundary>
        </div>
    );
};

export default StudioWidget;
